// src/pages/StaffDashboard.tsx
// Reklamcı / müşteri iletişim staff'ı için panel.
// ÖNEMLİ: Bu ekran KASITLI olarak hiçbir kazanç/komisyon/tutar bilgisi göstermez.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Users, Search, Phone, Mail, MessageCircle, Calendar, Clock, LogOut,
  X, Package, Puzzle, StickyNote, Send, Trash2, RefreshCw, Bell, ChevronLeft, ChevronRight, TrendingUp, Download,
} from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import { apiService } from '../services/api';
import type { StaffCustomer, StaffCustomerDetail, StaffStats, StaffContactLogEntry, StaffCalendarItem } from '../services/api';

interface StaffDashboardProps {
  onLogout?: () => void;
  onBack?: () => void; // admin önizlemesinde "Admin'e dön" için
}

// ---- yardımcılar ----
const STATUS_META: Record<string, { label: string; cls: string; dot: string }> = {
  sicak: { label: 'Sıcak', cls: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-500' },
  soguk: { label: 'Soğuk', cls: 'bg-sky-50 text-sky-700 border-sky-200', dot: 'bg-sky-500' },
  aktif: { label: 'Aktif', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
};
const CHANNEL_META: Record<string, { label: string; icon: any }> = {
  whatsapp: { label: 'WhatsApp', icon: MessageCircle },
  phone: { label: 'Telefon', icon: Phone },
  email: { label: 'E-posta', icon: Mail },
  note: { label: 'Not', icon: StickyNote },
};

function waLink(phone?: string | null): string | null {
  if (!phone) return null;
  const digits = phone.replace(/[^\d]/g, '');
  return digits ? `https://wa.me/${digits}` : null;
}
function fmtTime(iso?: string | null): string {
  if (!iso) return '';
  const s = iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
}
function fmtLongDay(d: Date): string {
  return d.toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long' });
}
function fmtDate(iso?: string | null, withTime = false): string {
  if (!iso) return '—';
  const s = iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('tr-TR', withTime
    ? { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }
    : { day: '2-digit', month: '2-digit', year: 'numeric' });
}
function relTime(iso?: string | null): string {
  if (!iso) return 'Hiç';
  const s = iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z';
  const diff = Date.now() - new Date(s).getTime();
  const m = Math.floor(diff / 60000), h = Math.floor(diff / 3600000), d = Math.floor(diff / 86400000);
  if (m < 1) return 'Az önce';
  if (m < 60) return `${m} dk önce`;
  if (h < 24) return `${h} saat önce`;
  if (d === 1) return 'Dün';
  if (d < 30) return `${d} gün önce`;
  return fmtDate(iso);
}
// UTC ISO -> datetime-local input değeri (yerel saat)
function toLocalInput(iso?: string | null): string {
  if (!iso) return '';
  const s = iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '';
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60000);
  return local.toISOString().slice(0, 16);
}
// Kaynak raporu tarih aralığı presetleri
type SrcPreset = 'today' | 'week' | 'month' | 'all';
const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
function srcRange(p: SrcPreset): { s?: string; e?: string } {
  const now = new Date(); const today = ymd(now);
  if (p === 'today') return { s: today, e: today };
  if (p === 'week') { const d = new Date(now); d.setDate(d.getDate() - 6); return { s: ymd(d), e: today }; }
  if (p === 'month') { const d = new Date(now.getFullYear(), now.getMonth(), 1); return { s: ymd(d), e: today }; }
  return { s: '2020-01-01', e: today };
}

function isOverdueOrToday(iso?: string | null): boolean {
  if (!iso) return false;
  const s = iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z';
  const d = new Date(s);
  const tomorrow = new Date(); tomorrow.setHours(0, 0, 0, 0); tomorrow.setDate(tomorrow.getDate() + 1);
  return d.getTime() < tomorrow.getTime();
}

function StatusBadge({ status }: { status?: string | null }) {
  if (!status || !STATUS_META[status]) {
    return <span className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs text-gray-500">Belirsiz</span>;
  }
  const m = STATUS_META[status];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium ${m.cls}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} />{m.label}
    </span>
  );
}

export default function StaffDashboard({ onLogout, onBack }: StaffDashboardProps) {
  const [tab, setTab] = useState<'customers' | 'calendar' | 'sources'>('customers');
  const [stats, setStats] = useState<StaffStats | null>(null);
  const [sources, setSources] = useState<string[]>([]);

  // liste
  const [customers, setCustomers] = useState<StaffCustomer[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(false);

  // filtreler
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState(''); // '' | 'active' | 'passive' (User.IsActive)
  const [sourceFilter, setSourceFilter] = useState('');
  const [sortDir, setSortDir] = useState<'' | 'desc' | 'asc'>(''); // kayıt tarihi 3 durumlu
  const [exporting, setExporting] = useState(false);

  // Ekrandaki filtrelerle TÜM listeyi (sayfalamasız) Excel'e indir. Kazanç alanı yok — sunucu sınırlıyor.
  const exportCustomers = async () => {
    setExporting(true);
    try {
      await apiService.downloadStaffCustomersCsv({
        search: search || undefined,
        active: activeFilter || undefined,
        source: sourceFilter || undefined,
      });
    } catch (e: any) { toast.error(e?.message || 'Liste indirilemedi'); }
    finally { setExporting(false); }
  };

  // görüşme takvimi
  const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const [monthCursor, setMonthCursor] = useState<Date>(() => startOfMonth(new Date()));
  const [selectedDay, setSelectedDay] = useState<Date>(() => startOfDay(new Date()));
  const [calItems, setCalItems] = useState<StaffCalendarItem[]>([]);

  // kaynak raporu
  const [srcPreset, setSrcPreset] = useState<SrcPreset>('all');
  const [srcData, setSrcData] = useState<{ total: number; sources: { source: string; count: number }[] } | null>(null);

  // detay drawer
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<StaffCustomerDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [crmStatus, setCrmStatus] = useState('');
  const [crmNextCall, setCrmNextCall] = useState('');
  const [crmTopic, setCrmTopic] = useState('');
  const [crmNotes, setCrmNotes] = useState('');
  const [savingCrm, setSavingCrm] = useState(false);
  const [editName, setEditName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [togglingActive, setTogglingActive] = useState(false);
  const [logChannel, setLogChannel] = useState('phone');
  const [logNote, setLogNote] = useState('');
  const [addingLog, setAddingLog] = useState(false);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const loadStats = useCallback(async () => {
    try { setStats(await apiService.getStaffStats()); } catch { /* sessiz */ }
  }, []);
  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => {
    apiService.getStaffSources().then(r => setSources(r.sources || [])).catch(() => {});
  }, []);

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.getStaffCustomers({
        page, pageSize,
        search: search || undefined,
        active: activeFilter || undefined,
        source: sourceFilter || undefined,
        sort: sortDir ? 'createdAt' : undefined,
        dir: sortDir || undefined,
      });
      setCustomers(res.customers); setTotal(res.total);
    } catch (e: any) { toast.error(e?.message || 'Müşteriler yüklenemedi'); }
    finally { setLoading(false); }
  }, [page, pageSize, search, activeFilter, sourceFilter, sortDir]);

  useEffect(() => { if (tab === 'customers') loadCustomers(); }, [tab, page, activeFilter, sourceFilter, sortDir]); // eslint-disable-line

  const cycleSort = () => { setPage(1); setSortDir(d => d === '' ? 'desc' : d === 'desc' ? 'asc' : ''); };
  // arama debounce
  useEffect(() => {
    if (tab !== 'customers') return;
    const t = setTimeout(() => { setPage(1); loadCustomers(); }, 450);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  const loadCalendar = useCallback(async (monthStart: Date) => {
    setLoading(true);
    try {
      const from = new Date(monthStart.getFullYear(), monthStart.getMonth(), 1);
      const to = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 1);
      const r = await apiService.getStaffCalendar(from.toISOString(), to.toISOString());
      setCalItems(r.items);
    } catch (e: any) { toast.error(e?.message || 'Takvim yüklenemedi'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (tab === 'calendar') loadCalendar(monthCursor); }, [tab, monthCursor, loadCalendar]);

  // takvim yardımcıları
  const itemsByDay = useMemo(() => {
    const m = new Map<string, StaffCalendarItem[]>();
    for (const it of calItems) {
      const iso = it.nextCallAtUtc.endsWith('Z') || it.nextCallAtUtc.includes('+') ? it.nextCallAtUtc : it.nextCallAtUtc + 'Z';
      const d = new Date(iso);
      const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const arr = m.get(k); if (arr) arr.push(it); else m.set(k, [it]);
    }
    return m;
  }, [calItems]);
  const grid = useMemo(() => {
    const first = new Date(monthCursor.getFullYear(), monthCursor.getMonth(), 1);
    const offset = (first.getDay() + 6) % 7; // Pazartesi başlangıç
    const start = new Date(first.getFullYear(), first.getMonth(), 1 - offset);
    return Array.from({ length: 42 }, (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
  }, [monthCursor]);
  const selectedDayItems = useMemo(() =>
    (itemsByDay.get(ymd(selectedDay)) || []).slice().sort((a, b) => a.nextCallAtUtc.localeCompare(b.nextCallAtUtc)),
    [itemsByDay, selectedDay]);

  const loadSources2 = useCallback(async (p: SrcPreset) => {
    setLoading(true);
    try {
      const r = srcRange(p);
      const res = await apiService.getStaffSourceReport(r.s, r.e);
      // "(doğrudan)" (kaynaksız kayıtlar) gösterilmez — sadece reklam kaynakları
      const list = (res?.sources || []).filter(s => s.source !== '(doğrudan)');
      setSrcData({ total: list.reduce((a, s) => a + s.count, 0), sources: list });
    } catch (e: any) { toast.error(e?.message || 'Kaynak raporu yüklenemedi'); setSrcData(null); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (tab === 'sources') loadSources2(srcPreset); }, [tab, srcPreset, loadSources2]);

  const openDetail = async (id: string) => {
    setDetailId(id); setDetail(null); setDetailLoading(true);
    try {
      const d = await apiService.getStaffCustomer(id);
      setDetail(d);
      setCrmStatus(d.crm.status || '');
      setCrmNextCall(toLocalInput(d.crm.nextCallAtUtc));
      setCrmTopic(d.crm.nextCallTopic || '');
      setCrmNotes(d.crm.notes || '');
      setEditName(d.customer.fullName || '');
      setEditPhone(d.customer.phoneNumber || '');
      setLogChannel('phone'); setLogNote('');
    } catch (e: any) { toast.error(e?.message || 'Detay yüklenemedi'); setDetailId(null); }
    finally { setDetailLoading(false); }
  };
  const closeDetail = () => { setDetailId(null); setDetail(null); };

  const saveCrm = async () => {
    if (!detailId) return;
    setSavingCrm(true);
    try {
      const nextCallAtUtc = crmNextCall ? new Date(crmNextCall).toISOString() : null;
      const nextCallTopic = crmTopic.trim() || null;
      await apiService.updateStaffCrm(detailId, { status: crmStatus || null, nextCallAtUtc, nextCallTopic, notes: crmNotes || null });
      toast.success('Kaydedildi');
      loadCustomers(); loadStats();
      if (tab === 'calendar') loadCalendar(monthCursor);
      setDetail(d => d ? { ...d, crm: { ...d.crm, status: crmStatus || null, nextCallAtUtc, nextCallTopic, notes: crmNotes || null } } : d);
    } catch (e: any) { toast.error(e?.message || 'Kaydedilemedi'); }
    finally { setSavingCrm(false); }
  };

  const saveProfile = async () => {
    if (!detailId) return;
    if (!editName.trim()) { toast.error('Ad soyad boş olamaz'); return; }
    setSavingProfile(true);
    try {
      const res = await apiService.updateStaffProfile(detailId, { fullName: editName.trim(), phoneNumber: editPhone.trim() });
      toast.success('Bilgiler güncellendi');
      setDetail(d => d ? { ...d, customer: { ...d.customer, fullName: res.fullName, phoneNumber: res.phoneNumber ?? null } } : d);
      loadCustomers();
    } catch (e: any) { toast.error(e?.message || 'Güncellenemedi'); }
    finally { setSavingProfile(false); }
  };

  const toggleActive = async () => {
    if (!detailId || !detail) return;
    const next = !detail.customer.isActive;
    if (!next && !window.confirm(`"${detail.customer.fullName}" hesabı pasife alınacak. Emin misin?`)) return;
    setTogglingActive(true);
    try {
      const res = await apiService.setStaffCustomerActive(detailId, next);
      setDetail(d => d ? { ...d, customer: { ...d.customer, isActive: res.isActive } } : d);
      setCustomers(list => list.map(c => c.id === detailId ? { ...c, isActive: res.isActive } : c));
      toast.success(res.isActive ? 'Hesap aktifleştirildi' : 'Hesap pasife alındı');
      loadStats();
    } catch (e: any) { toast.error(e?.message || 'Değiştirilemedi'); }
    finally { setTogglingActive(false); }
  };

  const addLog = async () => {
    if (!detailId || (!logNote.trim() && logChannel === 'note')) { toast.error('Bir not girin'); return; }
    setAddingLog(true);
    try {
      const entry = await apiService.addStaffContactLog(detailId, { channel: logChannel, note: logNote.trim() || undefined });
      setDetail(d => d ? { ...d, contactLog: [entry, ...d.contactLog], crm: { ...d.crm, lastContactAtUtc: entry.createdAtUtc } } : d);
      setLogNote('');
      toast.success('Görüşme eklendi');
      loadCustomers();
    } catch (e: any) { toast.error(e?.message || 'Eklenemedi'); }
    finally { setAddingLog(false); }
  };

  const deleteLog = async (id: string) => {
    try {
      await apiService.deleteStaffContactLog(id);
      setDetail(d => d ? { ...d, contactLog: d.contactLog.filter(l => l.id !== id) } : d);
    } catch (e: any) { toast.error(e?.message || 'Silinemedi'); }
  };

  const statCards = useMemo(() => ([
    { label: 'Toplam Müşteri', value: stats?.totalCustomers ?? '—', icon: Users, color: 'text-indigo-600 bg-indigo-50' },
    { label: 'Aktif Müşteri', value: stats?.activeCustomers ?? '—', icon: Package, color: 'text-emerald-600 bg-emerald-50' },
    { label: 'Bugün Aranacak', value: stats?.dueToday ?? '—', icon: Bell, color: 'text-amber-600 bg-amber-50' },
    { label: 'Sipariş Veren', value: stats?.withOrders ?? '—', icon: Clock, color: 'text-sky-600 bg-sky-50' },
  ]), [stats]);

  const rows = customers;
  const trMonths = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
  const monthTitle = `${trMonths[monthCursor.getMonth()]} ${monthCursor.getFullYear()}`;

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800">
      <Toaster position="top-right" />

      {/* Üst bar */}
      <header className="sticky top-0 z-20 border-b border-gray-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6">
          <div className="flex items-center gap-3">
            <div className="grid h-9 w-9 place-items-center rounded-xl bg-indigo-600 text-white"><Users size={18} /></div>
            <div>
              <h1 className="text-base font-semibold leading-tight">Staff Paneli{onBack ? ' — Admin Önizleme' : ''}</h1>
              <p className="text-xs text-gray-500">Müşteri takip &amp; iletişim</p>
            </div>
          </div>
          {onBack ? (
            <button onClick={onBack} className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100">
              <ChevronLeft size={16} /> Admin'e dön
            </button>
          ) : (
            <button onClick={onLogout} className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100">
              <LogOut size={16} /> Çıkış
            </button>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
        {/* İstatistik kartları */}
        <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {statCards.map((c) => (
            <div key={c.label} className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-4">
              <div className={`grid h-10 w-10 place-items-center rounded-lg ${c.color}`}><c.icon size={18} /></div>
              <div>
                <div className="text-xl font-bold leading-none tabular-nums">{c.value}</div>
                <div className="mt-1 text-xs text-gray-500">{c.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Sekmeler */}
        <div className="mb-4 flex items-center gap-2">
          <button onClick={() => setTab('customers')} className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${tab === 'customers' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'}`}>
            <Users size={16} /> Müşteriler
          </button>
          <button onClick={() => setTab('calendar')} className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${tab === 'calendar' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'}`}>
            <Calendar size={16} /> Görüşme Takvimi {stats?.dueToday ? <span className="ml-1 rounded-full bg-amber-500 px-1.5 text-xs text-white">{stats.dueToday}</span> : null}
          </button>
          <button onClick={() => setTab('sources')} className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${tab === 'sources' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'}`}>
            <TrendingUp size={16} /> Kaynak Raporu
          </button>
          <button onClick={() => { tab === 'customers' ? loadCustomers() : tab === 'calendar' ? loadCalendar(monthCursor) : loadSources2(srcPreset); loadStats(); }} className="ml-auto inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-600 hover:bg-gray-100">
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} /> Yenile
          </button>
        </div>

        {/* Filtre çubuğu (sadece müşteriler) */}
        {tab === 'customers' && (
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[220px]">
              <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="İsim veya e-posta ara…" className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-indigo-400" />
            </div>
            <select value={activeFilter} onChange={e => { setPage(1); setActiveFilter(e.target.value); }} className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-400">
              <option value="">Tümü</option>
              <option value="active">Aktif</option>
              <option value="passive">Pasif</option>
            </select>
            <select value={sourceFilter} onChange={e => { setPage(1); setSourceFilter(e.target.value); }} className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-400">
              <option value="">Tüm kaynaklar</option>
              {sources.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <button
              onClick={exportCustomers}
              disabled={exporting}
              title="Ekrandaki filtrelere uyan tüm müşterileri Excel dosyası olarak indir"
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {exporting ? <RefreshCw size={15} className="animate-spin" /> : <Download size={15} />}
              {exporting ? 'Hazırlanıyor…' : "Excel'e indir"}
            </button>
          </div>
        )}

        {/* Kaynak Raporu */}
        {tab === 'sources' && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-white p-3">
              {([['today', 'Bugün'], ['week', 'Bu Hafta'], ['month', 'Bu Ay'], ['all', 'Tüm Zamanlar']] as [SrcPreset, string][]).map(([k, label]) => (
                <button key={k} onClick={() => setSrcPreset(k)} className={`rounded-lg px-3.5 py-2 text-sm font-medium ${srcPreset === k ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>{label}</button>
              ))}
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <p className="text-sm text-gray-500">Reklamdan Gelen Kayıt</p>
              <p className="text-2xl font-bold tabular-nums text-gray-800">{srcData?.total ?? '—'}</p>
            </div>

            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
              {loading && !srcData ? (
                <div className="py-12 text-center text-gray-400">Yükleniyor…</div>
              ) : !srcData || srcData.sources.length === 0 ? (
                <p className="py-8 text-center text-sm text-gray-500">Bu aralıkta reklam kaynaklı kayıt yok.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-3 font-medium">Kaynak</th>
                      <th className="px-4 py-3 text-right font-medium">Kayıt</th>
                      <th className="px-4 py-3 text-right font-medium">Oran</th>
                    </tr>
                  </thead>
                  <tbody>
                    {srcData.sources.map((s, i) => (
                      <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-800">{s.source}</td>
                        <td className="px-4 py-3 text-right font-semibold tabular-nums text-gray-800">{s.count}</td>
                        <td className="px-4 py-3 text-right text-gray-500 tabular-nums">{srcData.total > 0 ? Math.round((s.count / srcData.total) * 100) : 0}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* Görüşme Takvimi */}
        {tab === 'calendar' && (
          <div className="space-y-4">
            {/* hızlı seçim + ay gezinme */}
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-white p-3">
              <button onClick={() => { const t = startOfDay(new Date()); setSelectedDay(t); setMonthCursor(startOfMonth(t)); }} className="rounded-lg bg-gray-100 px-3.5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200">Bugün</button>
              <button onClick={() => { const t = new Date(); t.setDate(t.getDate() + 1); const s = startOfDay(t); setSelectedDay(s); setMonthCursor(startOfMonth(s)); }} className="rounded-lg bg-gray-100 px-3.5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200">Yarın</button>
              <input type="date" value={ymd(selectedDay)} onChange={e => { if (!e.target.value) return; const [y, m, d] = e.target.value.split('-').map(Number); const s = new Date(y, m - 1, d); setSelectedDay(s); setMonthCursor(startOfMonth(s)); }} className="rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-indigo-400" />
              <div className="ml-auto flex items-center gap-2">
                <button onClick={() => setMonthCursor(new Date(monthCursor.getFullYear(), monthCursor.getMonth() - 1, 1))} className="grid h-8 w-8 place-items-center rounded-lg border border-gray-200 hover:bg-gray-100"><ChevronLeft size={16} /></button>
                <span className="min-w-[130px] text-center text-sm font-semibold text-gray-700">{monthTitle}</span>
                <button onClick={() => setMonthCursor(new Date(monthCursor.getFullYear(), monthCursor.getMonth() + 1, 1))} className="grid h-8 w-8 place-items-center rounded-lg border border-gray-200 hover:bg-gray-100"><ChevronRight size={16} /></button>
              </div>
            </div>

            {/* takvim gridi */}
            <div className="rounded-xl border border-gray-200 bg-white p-3">
              <div className="mb-1 grid grid-cols-7 gap-1 text-center text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                {['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'].map(d => <div key={d}>{d}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {grid.map((day, i) => {
                  const key = ymd(day);
                  const inMonth = day.getMonth() === monthCursor.getMonth();
                  const isToday = key === ymd(new Date());
                  const isSel = key === ymd(selectedDay);
                  const cnt = (itemsByDay.get(key) || []).length;
                  return (
                    <button key={i} onClick={() => setSelectedDay(startOfDay(day))}
                      className={`flex h-16 flex-col items-start rounded-lg border p-1.5 text-left transition
                        ${isSel ? 'border-indigo-500 ring-1 ring-indigo-300' : 'border-gray-100'}
                        ${inMonth ? 'bg-white hover:bg-indigo-50/50' : 'bg-gray-50/60 text-gray-300'}`}>
                      <span className={`text-xs ${isToday ? 'grid h-5 w-5 place-items-center rounded-full bg-indigo-600 font-semibold text-white' : inMonth ? 'text-gray-700' : ''}`}>{day.getDate()}</span>
                      {cnt > 0 && (
                        <span className="mt-auto inline-flex items-center rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">{cnt} görüşme</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* seçili günün görüşmeleri */}
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
              <div className="border-b border-gray-100 px-4 py-3 text-sm font-semibold text-gray-700">
                {fmtLongDay(selectedDay)} · {selectedDayItems.length} görüşme
              </div>
              {selectedDayItems.length === 0 ? (
                <p className="py-8 text-center text-sm text-gray-400">Bu güne planlı görüşme yok</p>
              ) : (
                <div className="divide-y divide-gray-100">
                  {selectedDayItems.map(it => {
                    const wa = waLink(it.phoneNumber);
                    return (
                      <div key={it.id} onClick={() => openDetail(it.id)} className="flex cursor-pointer items-center gap-3 px-4 py-3 hover:bg-indigo-50/40">
                        <div className="w-12 flex-none text-sm font-semibold tabular-nums text-indigo-600">{fmtTime(it.nextCallAtUtc)}</div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2"><span className="truncate font-medium text-gray-900">{it.fullName}</span><StatusBadge status={it.status} /></div>
                          <div className="truncate text-xs text-gray-500">{it.topic || 'Konu belirtilmemiş'}</div>
                        </div>
                        {wa && <a href={wa} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} title="WhatsApp" className="grid h-7 w-7 flex-none place-items-center rounded-md bg-emerald-50 text-emerald-600 hover:bg-emerald-100"><MessageCircle size={14} /></a>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tablo (müşteriler) */}
        {tab === 'customers' && (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                  <th className="px-4 py-3 font-medium">Müşteri</th>
                  <th className="px-4 py-3 font-medium">İletişim</th>
                  <th className="px-4 py-3 font-medium">Kaynak</th>
                  <th className="px-4 py-3 font-medium">Referans</th>
                  <th className="px-4 py-3 font-medium">Durum</th>
                  <th className="px-4 py-3 font-medium">Sipariş</th>
                  <th className="px-4 py-3 font-medium">
                    <button onClick={cycleSort} className="inline-flex items-center gap-1 uppercase hover:text-indigo-600">
                      Kayıt Tarihi
                      <span className="text-[11px]">{sortDir === 'desc' ? '↓' : sortDir === 'asc' ? '↑' : '↕'}</span>
                    </button>
                  </th>
                  <th className="px-4 py-3 font-medium">Tekrar Ara</th>
                </tr>
              </thead>
              <tbody>
                {loading && rows.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-400">Yükleniyor…</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-400">Müşteri bulunamadı</td></tr>
                ) : rows.map((c) => {
                  const wa = waLink(c.phoneNumber);
                  const due = isOverdueOrToday(c.nextCallAtUtc);
                  return (
                    <tr key={c.id} onClick={() => openDetail(c.id)} className="cursor-pointer border-b border-gray-100 hover:bg-indigo-50/40">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{c.fullName}</span>
                          {c.isActive === true && <span className="rounded-full border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">Aktif</span>}
                          {c.isActive === false && <span className="rounded-full border border-rose-200 bg-rose-50 px-1.5 py-0.5 text-[10px] font-medium text-rose-600">Pasif</span>}
                        </div>
                        <div className="text-xs text-gray-500">{c.email}</div>
                      </td>
                      <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-1.5">
                          {c.phoneNumber ? <span className="text-xs text-gray-600">{c.phoneNumber}</span> : <span className="text-xs text-gray-400">—</span>}
                          {wa && <a href={wa} target="_blank" rel="noreferrer" title="WhatsApp" className="grid h-6 w-6 place-items-center rounded-md bg-emerald-50 text-emerald-600 hover:bg-emerald-100"><MessageCircle size={13} /></a>}
                        </div>
                      </td>
                      <td className="px-4 py-3">{c.signupSource ? <span className="rounded-md bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{c.signupSource}</span> : <span className="text-xs text-gray-400">Doğrudan</span>}</td>
                      <td className="px-4 py-3">{c.referredByName ? <span className="text-xs text-gray-700">{c.referredByName}</span> : <span className="text-xs text-gray-400">—</span>}</td>
                      <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                      <td className="px-4 py-3">
                        {'orderCount' in c ? (
                          <div>
                            <div className="text-sm font-medium tabular-nums">{(c as StaffCustomer).orderCount} adet</div>
                            <div className="text-xs text-gray-400">{(c as StaffCustomer).lastOrderDateUtc ? `son: ${fmtDate((c as StaffCustomer).lastOrderDateUtc)}` : 'sipariş yok'}</div>
                          </div>
                        ) : <span className="text-xs text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3"><span className="text-xs text-gray-600">{fmtDate(c.createdAtUtc)}</span></td>
                      <td className="px-4 py-3">
                        {c.nextCallAtUtc
                          ? <span className={`inline-flex items-center gap-1 text-xs ${due ? 'font-semibold text-amber-600' : 'text-gray-600'}`}><Calendar size={12} />{fmtDate(c.nextCallAtUtc, true)}</span>
                          : <span className="text-xs text-gray-400">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Sayfalama (müşteriler) */}
          {tab === 'customers' && total > pageSize && (
            <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 text-sm text-gray-500">
              <span>{total} müşteri · sayfa {page}/{totalPages}</span>
              <div className="flex items-center gap-1">
                <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} className="grid h-8 w-8 place-items-center rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-100"><ChevronLeft size={16} /></button>
                <button disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} className="grid h-8 w-8 place-items-center rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-100"><ChevronRight size={16} /></button>
              </div>
            </div>
          )}
        </div>
        )}
      </main>

      {/* Detay drawer */}
      {detailId && (
        <div className="fixed inset-0 z-40 flex justify-end">
          <div className="absolute inset-0 bg-black/30" onClick={closeDetail} />
          <div className="relative flex h-full w-full max-w-md flex-col overflow-y-auto bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-5 py-4">
              <h2 className="text-sm font-semibold text-gray-800">Müşteri Detayı</h2>
              <button onClick={closeDetail} className="grid h-8 w-8 place-items-center rounded-lg text-gray-500 hover:bg-gray-100"><X size={18} /></button>
            </div>

            {detailLoading || !detail ? (
              <div className="p-10 text-center text-gray-400">Yükleniyor…</div>
            ) : (
              <div className="flex flex-col gap-5 p-5">
                {/* Kimlik */}
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-semibold text-gray-900">{detail.customer.fullName}</span>
                    {detail.customer.isActive
                      ? <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">Aktif hesap</span>
                      : <span className="rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-xs font-medium text-rose-600">Pasif hesap</span>}
                    <button
                      onClick={toggleActive}
                      disabled={togglingActive}
                      title={detail.customer.isActive ? 'Hesabı pasife al' : 'Hesabı aktifleştir'}
                      className={`ml-auto rounded-lg border px-2.5 py-1 text-xs font-medium disabled:opacity-60 ${detail.customer.isActive
                        ? 'border-rose-200 bg-rose-50 text-rose-600 hover:bg-rose-100'
                        : 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`}>
                      {togglingActive ? '…' : detail.customer.isActive ? 'Pasife al' : 'Aktifleştir'}
                    </button>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-gray-600">
                    <span className="inline-flex items-center gap-1.5"><Mail size={14} className="text-gray-400" />{detail.customer.email}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {detail.customer.phoneNumber && <span className="inline-flex items-center gap-1.5 text-sm text-gray-600"><Phone size={14} className="text-gray-400" />{detail.customer.phoneNumber}</span>}
                    {waLink(detail.customer.phoneNumber) && (
                      <a href={waLink(detail.customer.phoneNumber)!} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500 px-2.5 py-1 text-xs font-medium text-white hover:bg-emerald-600">
                        <MessageCircle size={13} /> WhatsApp
                      </a>
                    )}
                  </div>
                  <div className="mt-2 text-xs text-gray-400">
                    Kaynak: {detail.customer.signupSource || 'Doğrudan'} · Kayıt: {fmtDate(detail.customer.createdAtUtc)}
                  </div>
                  {detail.referral && (
                    <div className="mt-1 text-xs text-gray-500">
                      Getiren: {detail.referral.referredBy ? <span className="font-medium text-gray-700">{detail.referral.referredBy.fullName}</span> : <span className="text-gray-400">Yok</span>}
                      {detail.referral.referredCount > 0 && <> · Getirdiği: <span className="font-medium text-gray-700">{detail.referral.referredCount} kişi</span></>}
                    </div>
                  )}
                </div>

                {/* Bilgi düzenleme — isim + telefon (e-posta değiştirilemez) */}
                <div className="rounded-xl border border-gray-200 p-4">
                  <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-400">Bilgiler</div>
                  <label className="mb-1 block text-xs text-gray-500">Ad Soyad</label>
                  <input type="text" value={editName} onChange={e => setEditName(e.target.value)} className="mb-3 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-indigo-400" />
                  <label className="mb-1 block text-xs text-gray-500">Telefon</label>
                  <input type="text" value={editPhone} onChange={e => setEditPhone(e.target.value)} placeholder="+90XXXXXXXXXX" className="mb-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-indigo-400" />
                  <p className="mb-3 text-[11px] text-gray-400">E-posta değiştirilemez (giriş kimliği).</p>
                  <button onClick={saveProfile} disabled={savingProfile} className="w-full rounded-lg border border-indigo-200 bg-indigo-50 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-100 disabled:opacity-60">{savingProfile ? 'Kaydediliyor…' : 'Bilgileri Kaydet'}</button>
                </div>

                {/* Aktiflik (para yok) */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-lg border border-gray-200 p-3">
                    <div className="flex items-center gap-1.5 text-xs text-gray-500"><Package size={13} /> Sipariş</div>
                    <div className="mt-1 text-lg font-bold tabular-nums">{detail.activation.orderCount}</div>
                    <div className="text-xs text-gray-400">{detail.activation.lastOrderDateUtc ? `son: ${fmtDate(detail.activation.lastOrderDateUtc)}` : 'sipariş yok'}</div>
                  </div>
                  <div className="rounded-lg border border-gray-200 p-3">
                    <div className="flex items-center gap-1.5 text-xs text-gray-500"><Puzzle size={13} /> Eklenti</div>
                    <div className={`mt-1 text-sm font-semibold ${detail.activation.hasPlugin ? 'text-emerald-600' : 'text-gray-400'}`}>{detail.activation.hasPlugin ? 'Kurulu' : 'Kurulu değil'}</div>
                    <div className="text-xs text-gray-400">{detail.activation.pluginSoftware || (detail.activation.lastActivityUtc ? `aktif: ${relTime(detail.activation.lastActivityUtc)}` : '')}</div>
                  </div>
                  <div className="col-span-2 rounded-lg border border-gray-200 p-3">
                    <div className="flex items-center gap-1.5 text-xs text-gray-500"><Clock size={13} /> Siteye son görülme</div>
                    <div className="mt-1 text-sm font-semibold text-gray-700">{detail.activation.lastSeenWebAtUtc ? relTime(detail.activation.lastSeenWebAtUtc) : 'Hiç girmemiş'}</div>
                    {detail.activation.lastSeenWebAtUtc && <div className="text-xs text-gray-400">{fmtDate(detail.activation.lastSeenWebAtUtc, true)}</div>}
                  </div>
                </div>

                {/* CRM düzenleme */}
                <div className="rounded-xl border border-gray-200 p-4">
                  <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-400">Takip</div>
                  <label className="mb-1 block text-xs text-gray-500">Durum</label>
                  <div className="mb-3 flex gap-2">
                    {(['', 'sicak', 'soguk', 'aktif'] as const).map(s => (
                      <button key={s || 'none'} onClick={() => setCrmStatus(s)} className={`rounded-lg border px-2.5 py-1 text-xs font-medium ${crmStatus === s ? (s ? STATUS_META[s].cls : 'bg-gray-100 text-gray-700 border-gray-300') : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}>
                        {s ? STATUS_META[s].label : 'Belirsiz'}
                      </button>
                    ))}
                  </div>
                  <label className="mb-1 block text-xs text-gray-500">Görüşme tarihi (takvime düşer)</label>
                  <input type="datetime-local" value={crmNextCall} onChange={e => setCrmNextCall(e.target.value)} className="mb-3 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-indigo-400" />
                  <label className="mb-1 block text-xs text-gray-500">Görüşme konusu</label>
                  <input type="text" value={crmTopic} onChange={e => setCrmTopic(e.target.value)} maxLength={200} placeholder="ör. fiyat teklifi, takip araması…" className="mb-3 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-indigo-400" />
                  <label className="mb-1 block text-xs text-gray-500">Not</label>
                  <textarea value={crmNotes} onChange={e => setCrmNotes(e.target.value)} rows={3} placeholder="Müşteri hakkında notlar…" className="mb-3 w-full resize-y rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-indigo-400" />
                  <button onClick={saveCrm} disabled={savingCrm} className="w-full rounded-lg bg-indigo-600 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60">{savingCrm ? 'Kaydediliyor…' : 'Kaydet'}</button>
                </div>

                {/* Görüşme logu */}
                <div className="rounded-xl border border-gray-200 p-4">
                  <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-400">Görüşme Geçmişi</div>
                  <div className="mb-3 flex gap-2">
                    <select value={logChannel} onChange={e => setLogChannel(e.target.value)} className="rounded-lg border border-gray-200 px-2 py-2 text-sm outline-none focus:border-indigo-400">
                      {Object.entries(CHANNEL_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                    <input value={logNote} onChange={e => setLogNote(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addLog(); }} placeholder="Görüşme notu…" className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-indigo-400" />
                    <button onClick={addLog} disabled={addingLog} className="grid w-9 place-items-center rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60"><Send size={15} /></button>
                  </div>
                  <div className="flex flex-col gap-2">
                    {detail.contactLog.length === 0 ? (
                      <div className="py-4 text-center text-xs text-gray-400">Henüz görüşme yok</div>
                    ) : detail.contactLog.map(l => {
                      const cm = CHANNEL_META[l.channel] || CHANNEL_META.note;
                      return (
                        <div key={l.id} className="group flex items-start gap-2 rounded-lg bg-gray-50 p-2.5">
                          <div className="mt-0.5 grid h-6 w-6 flex-none place-items-center rounded-md bg-white text-gray-500"><cm.icon size={13} /></div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm text-gray-800">{l.note || <span className="text-gray-400">({cm.label})</span>}</div>
                            <div className="text-xs text-gray-400">{cm.label} · {relTime(l.createdAtUtc)}{l.staffName ? ` · ${l.staffName}` : ''}</div>
                          </div>
                          <button onClick={() => deleteLog(l.id)} className="opacity-0 transition group-hover:opacity-100 text-gray-400 hover:text-red-500"><Trash2 size={14} /></button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
